import {
  ChangeDetectionStrategy,
  Component,
  ChangeDetectorRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LucideAngularModule } from 'lucide-angular';
import { MenuItem } from '@core/models/menu.model';
import { MenuDrawerService } from '@core/services/menu-drawer.service';
import { AuthService } from '@core/services/auth.service';
import { LaboratoryService } from '@core/services/laboratory.service';
import { UserPhotoService } from '@core/services/user-photo.service';
import { User } from '@core/models/user.model';
import {
  isMobileReadyMenuItem,
  MOBILE_READY_FEATURE_KEYS,
  MOBILE_READY_ROUTE_PREFIXES,
} from '@core/config/mobile-ready-features';
import { opensAsOverlay, OverlaySubmoduleKey, SUBMODULE_KEY_CADASTRO_CLINICAS, SUBMODULE_KEY_CADASTRO_FILAS_COLETA, SUBMODULE_KEY_CADASTRO_PACIENTE, SUBMODULE_KEY_CADASTRO_SETORES_LABORATORIO, SUBMODULE_KEY_CADASTRO_SOLICITANTES, SUBMODULE_KEY_CADASTRO_TABELA_PRECO, SUBMODULE_KEY_ESTABELECIMENTO_SAUDE, SUBMODULE_KEY_VACINAS_CONFIGURACAO } from '@core/utilities/backend-menu.util';
import { HealthFacilitiesModalService } from '@app/features/health-facilities/services/health-facilities-modal.service';
import { CollectionQueuesModalService } from '@app/features/collection-queues/services/collection-queues-modal.service';
import { VaccinesConfigModalService } from '@app/features/vaccines-config/services/vaccines-config-modal.service';
import { LaboratorySectorsModalService } from '@app/features/laboratory-sectors/services/laboratory-sectors-modal.service';
import { SolicitantesModalService } from '@app/features/solicitantes/services/solicitantes-modal.service';
import { PriceTablesModalService } from '@app/features/price-tables/services/price-tables-modal.service';
import { ClinicsModalService } from '@app/features/clinics/services/clinics-modal.service';
import { PatientRegistryModalService } from '@app/features/patient/services/patient-registry-modal.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

/**
 * Mobile drawer menu (TASK-59145 Fase 5).
 *
 *  - Drill-down navigation: tapping a parent pushes onto a path stack;
 *    back arrow pops; X closes the drawer.
 *  - Allowlist filter: items absent from `MOBILE_READY_FEATURE_KEYS` (and
 *    not matched by any prefix in `MOBILE_READY_ROUTE_PREFIXES`) are
 *    dropped before rendering. Empty parents (children all filtered out)
 *    collapse to nothing.
 *  - Closes on ESC, backdrop click, and `NavigationEnd` so any in-app
 *    routerLink the user taps inside the drawer dismisses it cleanly.
 *  - Body scroll lock while open via the `is-drawer-open` body class.
 *
 * Open state lives on `MenuDrawerService` (root injector) so the header's
 * hamburger and the drawer (mounted by the layout) communicate without DOM
 * coupling.
 */
@Component({
    selector: 'app-menu-drawer',
    imports: [CommonModule, RouterModule, TranslatePipe, LucideAngularModule],
    templateUrl: './menu-drawer.component.html',
    styleUrl: './menu-drawer.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuDrawerComponent implements OnInit, OnDestroy {
  /** Backend-provided menu tree (pre-filtered to `MOBILE_READY_*`). */
  @Input() set items(value: MenuItem[]) {
    this._items = value ?? [];
    this.filteredRoot = this.filterTree(this._items);
    this.resetPathIfStale();
  }
  get items(): MenuItem[] {
    return this._items;
  }
  private _items: MenuItem[] = [];
  filteredRoot: MenuItem[] = [];

  /** Hint to highlight the active leaf on the current page. */
  @Input() currentUrl = '';

  /** Stack of parents currently drilled into. Empty = at root. */
  currentPath: MenuItem[] = [];

  private routerSubscription?: Subscription;

  /**
   * TASK-60341/US #60630: key → overlay-open action. Mirrors the same map on
   * `SidebarComponent`/`HomePageComponent` (code-quality review finding on
   * this same US — each entry point keeps its own map, because the modal
   * services it opens are this component's own dependencies, not shared
   * state). Without a branch here, `onOverlayLeafClick` would always open
   * the first overlay ever wired (Estabelecimento de Saúde), even for a
   * leaf whose key is Filas de Coleta.
   *
   * Keyed by `OverlaySubmoduleKey`, so the map is EXHAUSTIVE over
   * `OVERLAY_SUBMODULE_KEY_LIST` by type: a key added to that list and forgotten
   * here does not compile. Do not widen the key back to `string` — that is what
   * turns the omission into a drawer leaf that closes the drawer and does nothing.
   */
  private readonly overlayOpeners: Readonly<Record<OverlaySubmoduleKey, () => void>> = {
    [SUBMODULE_KEY_ESTABELECIMENTO_SAUDE]: () => this.healthFacilitiesModal.open(),
    [SUBMODULE_KEY_CADASTRO_FILAS_COLETA]: () => this.collectionQueuesModal.open(),
    [SUBMODULE_KEY_CADASTRO_CLINICAS]: () => this.clinicsModal.open(),
    [SUBMODULE_KEY_VACINAS_CONFIGURACAO]: () => this.vaccinesConfigModal.open(),
    [SUBMODULE_KEY_CADASTRO_SETORES_LABORATORIO]: () => this.laboratorySectorsModal.open(),
    [SUBMODULE_KEY_CADASTRO_SOLICITANTES]: () => this.solicitantesModal.open(),
    // US #62189: Cadastros › Tabela de Preços.
    [SUBMODULE_KEY_CADASTRO_TABELA_PRECO]: () => this.priceTablesModal.open(),
    [SUBMODULE_KEY_CADASTRO_PACIENTE]: () => this.patientRegistryModal.open(),
  };

  // ── User block (parity with desktop header avatar / sidebar identity) ──
  /** Currently authenticated user (null while logging in / after logout). */
  readonly currentUser$: Observable<User | null>;
  /** Tenant lab name shown under the user's name. */
  readonly laboratoryRazaoSocial$: Observable<string | null>;
  /** User photo URL (null falls back to initials). */
  readonly userPhoto$: Observable<string | null>;

  constructor(
    public drawer: MenuDrawerService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private laboratoryService: LaboratoryService,
    private userPhotoService: UserPhotoService,
    private healthFacilitiesModal: HealthFacilitiesModalService,
    private collectionQueuesModal: CollectionQueuesModalService,
    private vaccinesConfigModal: VaccinesConfigModalService,
    private laboratorySectorsModal: LaboratorySectorsModalService,
    private solicitantesModal: SolicitantesModalService,
    private priceTablesModal: PriceTablesModalService,
    private clinicsModal: ClinicsModalService,
    private patientRegistryModal: PatientRegistryModalService,
  ) {
    this.currentUser$ = this.authService.currentUser$;
    this.laboratoryRazaoSocial$ = this.laboratoryService.razaoSocial$;
    this.userPhoto$ = this.userPhotoService.getCurrentUserPhotoUrl();
    // Whenever the drawer signal flips, sync body class and reset path on
    // close so the next open starts fresh at root.
    effect(() => {
      const open = this.drawer.isOpen();
      document.body.classList.toggle('is-drawer-open', open);
      if (!open) {
        this.currentPath = [];
        this.cdr.markForCheck();
      }
    });
  }

  ngOnInit(): void {
    this.routerSubscription = this.router.events
      .pipe(filter(e => e instanceof NavigationEnd))
      .subscribe(() => this.drawer.close());
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    document.body.classList.remove('is-drawer-open');
  }

  /** Children to render for the current drill-down level. */
  get visibleItems(): MenuItem[] {
    const tail = this.currentPath.at(-1);
    if (!tail) return this.filteredRoot;
    return tail.children ?? [];
  }

  /** Title for the current level — root uses i18n; deeper levels use parent label. */
  get headerTitle(): string {
    const tail = this.currentPath.at(-1);
    return tail?.label ?? '';
  }

  /** Whether the mobile menu has nothing to show because the allowlist is empty here. */
  get isEmpty(): boolean {
    return this.filteredRoot.length === 0;
  }

  hasChildren(item: MenuItem): boolean {
    return !!item.children && item.children.length > 0;
  }

  /** Avatar fallback when the user has no photo — same logic as the header. */
  getInitials(name: string | undefined): string {
    return this.userPhotoService.getInitials(name ?? '');
  }

  /** Truncates the user's name to fit the drawer width. */
  truncateName(name: string | undefined): string {
    return this.userPhotoService.truncateName(name ?? '', 24);
  }

  /**
   * Lucide icon name for a menu item. Backend already maps icons via
   * `backendIconToLucide`, so we just consume `iconSvg`. Falls back to
   * `file-text` when the backend doesn't ship one — same default the
   * desktop sidebar uses.
   */
  getIconName(item: MenuItem): string {
    return item.iconSvg && item.iconSvg.trim() ? item.iconSvg : 'file-text';
  }

  /** Logs out and lets the auth service redirect to /login. */
  logout(): void {
    this.drawer.close();
    this.authService.logout();
  }

  trackById(_: number, item: MenuItem): string | number {
    return item.id ?? item.key ?? item.label;
  }

  goInto(item: MenuItem): void {
    if (!this.hasChildren(item)) return;
    this.currentPath = [...this.currentPath, item];
    this.cdr.markForCheck();
  }

  goBack(): void {
    if (this.currentPath.length === 0) return;
    this.currentPath = this.currentPath.slice(0, -1);
    this.cdr.markForCheck();
  }

  closeDrawer(): void {
    this.drawer.close();
  }

  /**
   * TASK-60341: whether this leaf opens an in-app overlay (modal) instead of
   * navigating. Centralized in `backend-menu.util` so every entry point
   * (sidebar, this drawer, home quick-access) shares one rule.
   */
  opensAsOverlay(item: MenuItem): boolean {
    return opensAsOverlay(item.key);
  }

  /**
   * TASK-60341/TASK-60388/US #60630: overlay leaf tap (e.g. Estabelecimento de
   * Saúde, Setores Hospitalares, Filas de Coleta) — opens the matching modal
   * via `overlayOpeners` and closes the drawer. No navigation: the removed
   * /health-facilities route no longer exists, so this must never fall
   * through to a routerLink. Permission gating already hides the leaf when
   * the user lacks access, so reaching this handler means it is allowed.
   */
  onOverlayLeafClick(item: MenuItem): void {
    this.drawer.close();
    // The template only reaches this handler through `opensAsOverlay(item)`, so the
    // guard is re-stated here to NARROW the key: over `OverlaySubmoduleKey` the map
    // is exhaustive by type, and indexing it directly is what makes a forgotten
    // opener a compile error instead of a leaf that closes the drawer and does
    // nothing. Repeating the predicate is the price of that; an `?.()` on a
    // `string` key is not.
    if (!opensAsOverlay(item.key)) return;
    this.overlayOpeners[item.key]();
  }

  onLeafClick(_item: MenuItem): void {
    // Close synchronously *before* the routerLink fires its navigation.
    // Without this the user briefly sees the drawer reopen on the new
    // page: the old drawer is destroyed and the new one mounts during
    // ActivationEnd (before NavigationEnd), so the new drawer reads the
    // signal as still `true` and renders open until NavigationEnd lands
    // and the listener calls close(). Closing here flips the signal
    // first; the navigation proceeds with `isOpen === false`. The
    // NavigationEnd subscription stays as a safety net for cases that
    // bypass this handler (browser back, programmatic navigation).
    this.drawer.close();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) this.drawer.close();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.drawer.isOpen()) this.drawer.close();
  }

  // ── Filtering ─────────────────────────────────────────────────────────
  //
  // Recursively filters the menu tree against the mobile allowlist. A
  // parent survives only when at least one of its descendants is mobile-
  // ready — otherwise empty folders would render as dead-end taps.

  private filterTree(items: MenuItem[]): MenuItem[] {
    return items
      .map(item => this.filterItem(item))
      .filter((item): item is MenuItem => item !== null);
  }

  private filterItem(item: MenuItem): MenuItem | null {
    const isFolder = !!item.children && item.children.length > 0;

    // A folder is a grouping row and nothing else — the template gives
    // `hasChildren` priority over every leaf branch, so tapping one can only
    // drill in. It therefore survives on its CHILDREN, never on its own key:
    // an allowlisted folder with nothing left after filtering would be a tap
    // that goes nowhere. `children` is ALWAYS the filtered list; the old
    // fallback to `item.children` handed the ORIGINAL, unfiltered children
    // back whenever nothing survived, dumping every unadapted screen onto the
    // phone.
    if (isFolder) {
      const filteredChildren = this.filterTree(item.children!);
      return filteredChildren.length > 0 ? { ...item, children: filteredChildren } : null;
    }

    return isMobileReadyMenuItem(item) ? { ...item } : null;
  }

  /** Drops the path if the items input changed and a previously open parent vanished. */
  private resetPathIfStale(): void {
    if (this.currentPath.length === 0) return;
    let cursor: MenuItem[] = this.filteredRoot;
    const newPath: MenuItem[] = [];
    for (const step of this.currentPath) {
      const match = cursor.find(c => (c.id ?? c.key ?? c.label) === (step.id ?? step.key ?? step.label));
      if (!match) break;
      newPath.push(match);
      cursor = match.children ?? [];
    }
    this.currentPath = newPath;
  }

  // Exposed for tests / debug.
  static readonly _allowKeys = MOBILE_READY_FEATURE_KEYS;
  static readonly _allowPrefixes = MOBILE_READY_ROUTE_PREFIXES;
}
